// Sugalarm — Wi-Fi signal bridge wrapper (cycle 235.4).
//
// The single owner of access to the native `WifiSignal` plugin. That plugin
// exists ONLY in the Android kiosk APK (the `kiosk` product flavor); it is
// absent on web, the Pi kiosk, iPhone, the phone layout, and the `standard`
// Android flavor — so `isAvailable()` is false on every surface except the
// kiosk APK, and the header Wi-Fi indicator gates strictly on it. Mirrors
// `lib/battery.ts` exactly.
//
// Reached through the bridge-injected `window.Capacitor.Plugins` proxy —
// NEVER a bare `import("@capacitor/...")` (cycle 26/138 rule).
//
// READ-ONLY: reports the current Wi-Fi connection's signal strength. No
// write, no network, no secret, no PHI, no new permission, and it never
// reads the Wi-Fi password.

import { isCapacitorNative } from "./capacitor";

export interface WifiStatus {
  connected: boolean;
  /** 0–4 bars, or -1 when not connected / unreadable. */
  level: number;
  /** 0–100 derived from RSSI, or -1 when not connected / unreadable. */
  percent: number;
  ssid?: string;
}

interface PluginListenerHandle {
  remove: () => Promise<void> | void;
}

interface WifiSignalProxy {
  getStatus(): Promise<WifiStatus>;
  addListener(
    event: "wifiStatus",
    cb: (s: WifiStatus) => void,
  ): Promise<PluginListenerHandle> | PluginListenerHandle;
}

/** Resolve the natively-registered plugin proxy off the Capacitor bridge,
 *  or null on any surface where it isn't present. */
function plugin(): WifiSignalProxy | null {
  if (!isCapacitorNative()) return null;
  const cap = (
    window as { Capacitor?: { Plugins?: Record<string, unknown> } }
  ).Capacitor;
  const p = cap?.Plugins?.WifiSignal;
  return p != null ? (p as WifiSignalProxy) : null;
}

/** True iff the native WifiSignal plugin is registered on the bridge — i.e.
 *  the SPA is running inside the Android kiosk APK (the tablet). The header
 *  Wi-Fi indicator renders only when this is true. */
export function isAvailable(): boolean {
  return plugin() != null;
}

/** Read the current Wi-Fi status, or null on a non-kiosk-APK surface / on any
 *  bridge error. */
export async function getStatus(): Promise<WifiStatus | null> {
  const p = plugin();
  if (p == null) return null;
  try {
    return await p.getStatus();
  } catch (err) {
    console.warn("[wifiSignal] getStatus failed", err);
    return null;
  }
}

/** Subscribe to live Wi-Fi changes; returns an unsubscribe. No-op (returns a
 *  no-op cleanup) when the plugin isn't available. */
export function subscribe(cb: (s: WifiStatus) => void): () => void {
  const p = plugin();
  if (p == null) return () => {};
  let handle: PluginListenerHandle | null = null;
  let cancelled = false;
  try {
    Promise.resolve(p.addListener("wifiStatus", cb))
      .then((h) => {
        if (cancelled) {
          try {
            void h?.remove?.();
          } catch {
            /* ignore */
          }
        } else {
          handle = h;
        }
      })
      .catch((err) => console.warn("[wifiSignal] addListener failed", err));
  } catch (err) {
    console.warn("[wifiSignal] subscribe failed", err);
  }
  return () => {
    cancelled = true;
    if (handle) {
      try {
        void handle.remove();
      } catch {
        /* ignore */
      }
      handle = null;
    }
  };
}
